// =======================
// src/paginas/modelos/componentes/catalogoModelosLista.js
// =======================

import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { ArrowDownward, ArrowUpward, UnfoldMore } from "@mui/icons-material";

import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";

import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";

import { separadorPaginacao } from "../../../estilos/paginacao";
import { ajustarTamanhoFonteSre } from "../../../estilos/global";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const GAP_CHIPS_PX = 5;

/*
 * =========================================================
 * DISTRIBUIÇÃO DAS COLUNAS
 * =========================================================
 *
 * SRE:
 * Nome       25%
 * Categoria  18%
 * Assunto    22%
 * Etapa      25%
 * Ações      10%
 */
const GRID_COLUNAS_SRE = "25% 18% 22% 25% 10%";

/*
 * Analista / Escola:
 *
 * Nome       25%
 * Categoria  18%
 * Assunto    24%
 * Etapa      26%
 * Baixar      7%
 */
const GRID_COLUNAS_CONSULTA = "25% 18% 24% 26% 7%";

/*
 * =========================================================
 * ESTILOS DA LISTA
 * =========================================================
 */

const ESTILO_TEXTO_DUAS_LINHAS = {
  minWidth: 0,

  lineHeight: 1.35,

  display: "-webkit-box",

  WebkitLineClamp: 2,

  WebkitBoxOrient: "vertical",

  overflow: "hidden",

  overflowWrap: "break-word",
};

const ESTILO_CHIP_ETAPA = {
  minHeight: 23,

  height: 23,

  maxWidth: "100%",

  backgroundColor: "#eaf4ff",

  color: "#0767d8",

  fontSize: ajustarTamanhoFonteSre("0.7rem"),

  "& .MuiChip-label": {
    px: 1,

    whiteSpace: "nowrap",

    lineHeight: 1.2,
  },
};

/*
 * =========================================================
 * ESTILOS DAS AÇÕES
 * =========================================================
 */

const criarEstiloAcao = ({
  cor,
  fundo,
  borda,
  corHover,
  fundoHover,
  sombraHover,
}) => ({
  width: 28,

  height: 28,

  p: 0,

  borderRadius: 1.2,

  color: cor,

  backgroundColor: fundo,

  border: `1px solid ${borda}`,

  transition:
    "color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease",

  "&:hover": {
    color: corHover,

    backgroundColor: fundoHover,

    borderColor: corHover,

    transform: "translateY(-1px)",

    boxShadow: sombraHover,
  },
});

const ESTILO_ACAO_DOWNLOAD = criarEstiloAcao({
  cor: "#00897b",

  fundo: "#e8f7f4",

  borda: "#ccece6",

  corHover: "#00695c",

  fundoHover: "#d5f0eb",

  sombraHover: "0 2px 5px rgba(0, 105, 92, 0.18)",
});

const ESTILO_ACAO_EDITAR = criarEstiloAcao({
  cor: "#1976d2",

  fundo: "#eaf3ff",

  borda: "#d0e5fb",

  corHover: "#0d5db8",

  fundoHover: "#dbeafe",

  sombraHover: "0 2px 5px rgba(25, 118, 210, 0.18)",
});

const ESTILO_ACAO_EXCLUIR = criarEstiloAcao({
  cor: "#d32f2f",

  fundo: "#fff0f0",

  borda: "#f8d7d7",

  corHover: "#b71c1c",

  fundoHover: "#ffe1e1",

  sombraHover: "0 2px 5px rgba(211, 47, 47, 0.18)",
});

/*
 * =========================================================
 * FUNÇÕES AUXILIARES
 * =========================================================
 */

const normalizarTexto = (valor = "") =>
  String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const obterIdEtapa = (etapa) => {
  if (typeof etapa === "object" && etapa !== null) {
    return etapa.id;
  }

  return etapa;
};

const obterNomeEtapa = (etapa, etapasCatalogo) => {
  if (typeof etapa === "object" && etapa !== null && etapa.nome) {
    return etapa.nome;
  }

  const etapaEncontrada = etapasCatalogo.find(
    (item) => String(item.id) === String(obterIdEtapa(etapa)),
  );

  return etapaEncontrada?.nome || "";
};

const obterEtapasModalidadesModelo = (modelo) => {
  const itens = [
    ...(Array.isArray(modelo.etapas) ? modelo.etapas : []),

    ...(Array.isArray(modelo.modalidades) ? modelo.modalidades : []),
  ];

  return [
    ...new Map(
      itens.map((item) => [String(obterIdEtapa(item)), item]),
    ).values(),
  ];
};

/*
 * Verifica se os chips cabem
 * em no máximo duas linhas.
 */
const cabeEmDuasLinhas = (larguras, larguraDisponivel, larguraExtra = 0) => {
  if (larguraDisponivel <= 0) {
    return false;
  }

  const itens = [...larguras];

  if (larguraExtra > 0) {
    itens.push(larguraExtra);
  }

  let linha = 1;

  let larguraUsada = 0;

  for (const larguraItem of itens) {
    if (larguraUsada === 0) {
      larguraUsada = larguraItem;

      continue;
    }

    if (larguraUsada + GAP_CHIPS_PX + larguraItem <= larguraDisponivel) {
      larguraUsada += GAP_CHIPS_PX + larguraItem;

      continue;
    }

    linha += 1;

    if (linha > 2) {
      return false;
    }

    larguraUsada = larguraItem;
  }

  return true;
};

/*
 * =========================================================
 * RÓTULO DOS FILTROS
 * =========================================================
 */

const RotuloCampo = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",

      mb: 0.55,

      color: "#4d6484",

      fontSize: ajustarTamanhoFonteSre("0.72rem"),

      fontWeight: 600,
    }}
  >
    {children}
  </Typography>
);

/*
 * =========================================================
 * FILTRO MÚLTIPLO
 * =========================================================
 */

const FiltroMultiplo = ({
  placeholder,
  itens,
  selecionados,
  onChange,
  icone,
}) => {
  const nomesPorId = useMemo(
    () => new Map(itens.map((item) => [String(item.id), item.nome])),

    [itens],
  );

  const handleChange = (event) => {
    const valor = event.target.value;

    onChange(typeof valor === "string" ? valor.split(",") : valor);
  };

  return (
    <FormControl
      fullWidth
      size="small"
      sx={{
        minWidth: 0,

        width: "100%",
      }}
    >
      <Select
        multiple
        displayEmpty
        value={selecionados}
        onChange={handleChange}
        input={
          <OutlinedInput
            startAdornment={
              icone ? (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      display: "flex",

                      alignItems: "center",

                      color: "#0767d8",
                    }}
                  >
                    {icone}
                  </Box>
                </InputAdornment>
              ) : null
            }
            sx={{
              minWidth: 0,
            }}
          />
        }
        renderValue={(valores) => {
          if (!Array.isArray(valores) || valores.length === 0) {
            return (
              <Typography
                component="span"
                variant="body2"
                noWrap
                sx={{
                  display: "block",

                  width: "100%",

                  minWidth: 0,

                  overflow: "hidden",

                  textOverflow: "ellipsis",

                  whiteSpace: "nowrap",

                  color: "#52606d",

                  fontSize: ajustarTamanhoFonteSre("0.82rem"),
                }}
              >
                {placeholder}
              </Typography>
            );
          }

          const nomes = valores
            .map((id) => nomesPorId.get(String(id)))
            .filter(Boolean);

          const textoSelecionado = nomes.join(", ");

          return (
            <Typography
              component="span"
              variant="body2"
              title={textoSelecionado}
              sx={{
                display: "block",

                width: "100%",

                maxWidth: "100%",

                minWidth: 0,

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",

                color: "#37474f",

                fontSize: ajustarTamanhoFonteSre("0.82rem"),
              }}
            >
              {textoSelecionado}
            </Typography>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 320,
            },
          },
        }}
        sx={{
          width: "100%",

          minWidth: 0,

          backgroundColor: "#fff",

          "& .MuiSelect-select": {
            minWidth: 0,

            maxWidth: "100%",

            py: 1,

            overflow: "hidden",

            textOverflow: "ellipsis",

            whiteSpace: "nowrap",
          },
        }}
      >
        {itens.map((item) => {
          const id = String(item.id);

          const selecionado = selecionados.includes(id);

          return (
            <MenuItem key={item.id} value={id}>
              <Checkbox size="small" checked={selecionado} />

              <ListItemText primary={item.nome} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

/*
 * =========================================================
 * ETAPAS / MODALIDADES COMPACTAS
 * =========================================================
 *
 * Mostra tudo o que couber em
 * até duas linhas.
 *
 * O restante fica no chip +N.
 */

const EtapasCompactas = ({ modelo, etapasCatalogo }) => {
  const containerRef = useRef(null);

  const medicaoRef = useRef(null);

  const [quantidadeVisivel, setQuantidadeVisivel] = useState(0);

  const itens = useMemo(
    () =>
      obterEtapasModalidadesModelo(modelo)
        .map((item) => ({
          id: obterIdEtapa(item),

          nome:
            obterNomeEtapa(item, etapasCatalogo) ||
            "Etapa/modalidade não identificada",
        }))
        .sort((a, b) =>
          String(a.nome).localeCompare(String(b.nome), "pt-BR", {
            sensitivity: "base",

            numeric: true,
          }),
        ),

    [modelo, etapasCatalogo],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;

    const areaMedicao = medicaoRef.current;

    if (!container || !areaMedicao) {
      return undefined;
    }

    let quadro = null;

    const calcular = () => {
      if (quadro) {
        cancelAnimationFrame(quadro);
      }

      quadro = requestAnimationFrame(() => {
        const larguraDisponivel = container.clientWidth;

        if (!larguraDisponivel || itens.length === 0) {
          setQuantidadeVisivel(itens.length);

          return;
        }

        const elementosChips = areaMedicao.querySelectorAll(
          '[data-etapa-medicao="true"]',
        );

        const chipMais = areaMedicao.querySelector(
          '[data-etapa-excedente="true"]',
        );

        const larguras = Array.from(elementosChips).map((elemento) =>
          Math.ceil(elemento.getBoundingClientRect().width),
        );

        const larguraMais = chipMais
          ? Math.ceil(chipMais.getBoundingClientRect().width)
          : 0;

        /*
         * Todos os chips cabem.
         */
        if (cabeEmDuasLinhas(larguras, larguraDisponivel)) {
          setQuantidadeVisivel(itens.length);

          return;
        }

        /*
         * Reserva espaço
         * para o chip +N.
         */
        let visiveis = itens.length - 1;

        while (visiveis > 0) {
          const largurasVisiveis = larguras.slice(0, visiveis);

          if (
            cabeEmDuasLinhas(largurasVisiveis, larguraDisponivel, larguraMais)
          ) {
            break;
          }

          visiveis -= 1;
        }

        setQuantidadeVisivel(visiveis);
      });
    };

    calcular();

    let observer = null;

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(calcular);

      observer.observe(container);
    } else {
      window.addEventListener("resize", calcular);
    }

    return () => {
      if (quadro) {
        cancelAnimationFrame(quadro);
      }

      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener("resize", calcular);
      }
    };
  }, [itens]);

  if (itens.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Não informado
      </Typography>
    );
  }

  const itensVisiveis = itens.slice(0, quantidadeVisivel);

  const itensOcultos = itens.slice(quantidadeVisivel);

  return (
    <Box
      sx={{
        position: "relative",

        width: "100%",

        minWidth: 0,
      }}
    >
      {/* ÁREA VISÍVEL */}

      <Box
        ref={containerRef}
        sx={{
          display: "flex",

          flexWrap: "wrap",

          alignItems: "center",

          gap: `${GAP_CHIPS_PX}px`,

          width: "100%",

          minWidth: 0,
        }}
      >
        {itensVisiveis.map((item) => (
          <Chip
            key={item.id}
            size="small"
            label={item.nome}
            sx={ESTILO_CHIP_ETAPA}
          />
        ))}

        {itensOcultos.length > 0 && (
          <Tooltip
            arrow
            placement="top"
            enterDelay={250}
            title={
              <Box
                sx={{
                  py: 0.5,

                  px: 0.25,

                  maxWidth: 360,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",

                    mb: 0.75,

                    color: "#fff",

                    fontWeight: 700,
                  }}
                >
                  Demais etapas/modalidades ({itensOcultos.length})
                </Typography>

                {itensOcultos.map((item) => (
                  <Typography
                    key={item.id}
                    variant="caption"
                    sx={{
                      display: "block",

                      color: "rgba(255,255,255,0.92)",

                      lineHeight: 1.5,
                    }}
                  >
                    {item.nome}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Chip
              size="small"
              label={`+${itensOcultos.length}`}
              sx={{
                height: 23,

                backgroundColor: "#e3f2fd",

                color: "#0767d8",

                fontSize: ajustarTamanhoFonteSre("0.7rem"),

                fontWeight: 700,

                cursor: "default",

                "& .MuiChip-label": {
                  px: 1,
                },

                "&:hover": {
                  backgroundColor: "#d7ebff",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* ÁREA INVISÍVEL PARA MEDIR OS CHIPS */}

      <Box
        ref={medicaoRef}
        aria-hidden="true"
        sx={{
          position: "fixed",

          left: -10000,

          top: -10000,

          display: "flex",

          alignItems: "center",

          visibility: "hidden",

          pointerEvents: "none",

          whiteSpace: "nowrap",
        }}
      >
        {itens.map((item) => (
          <Chip
            key={`medicao-${item.id}`}
            data-etapa-medicao="true"
            size="small"
            label={item.nome}
            sx={ESTILO_CHIP_ETAPA}
          />
        ))}

        <Chip
          data-etapa-excedente="true"
          size="small"
          label={`+${itens.length}`}
          sx={{
            height: 23,

            backgroundColor: "#e3f2fd",

            color: "#0767d8",

            fontSize: ajustarTamanhoFonteSre("0.7rem"),

            fontWeight: 700,

            "& .MuiChip-label": {
              px: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
};

/*
 * =========================================================
 * COMPONENTE PRINCIPAL
 * =========================================================
 */

const CatalogoModelosLista = ({
  isSuperintendent,
  visualPadronizado = isSuperintendent,
  modelos,
  categorias,
  assuntos,
  etapasModalidades = [],
  onDownload,
  onEditar,
  onExcluir,
}) => {
  /*
   * =======================================================
   * FILTROS
   * =======================================================
   */

  const [busca, setBusca] = useState("");

  const [categoriasFiltro, setCategoriasFiltro] = useState([]);

  const [assuntosFiltro, setAssuntosFiltro] = useState([]);

  const [etapasFiltro, setEtapasFiltro] = useState([]);

  /*
   * =======================================================
   * PAGINAÇÃO
   * =======================================================
   */

  const [pagina, setPagina] = useState(1);

  const [itensPorPagina, setItensPorPagina] = useState(5);

  /*
   * =======================================================
   * ORDENAÇÃO
   * =======================================================
   */

  const [ordem, setOrdem] = useState({
    coluna: null,

    direcao: "asc",
  });

  /*
   * =======================================================
   * DISTRIBUIÇÃO DAS COLUNAS POR PERFIL
   * =======================================================
   */

  const gridColunasModelos = isSuperintendent
    ? GRID_COLUNAS_SRE
    : GRID_COLUNAS_CONSULTA;

  const comparar = useMemo(
    () =>
      new Intl.Collator("pt-BR", {
        sensitivity: "base",

        numeric: true,
      }),

    [],
  );

  /*
   * =======================================================
   * ETAPAS / MODALIDADES
   * =======================================================
   */

  const etapas = useMemo(
    () =>
      [...etapasModalidades].sort((a, b) =>
        String(a.nome ?? "").localeCompare(String(b.nome ?? ""), "pt-BR"),
      ),

    [etapasModalidades],
  );

  /*
   * =======================================================
   * FILTRAGEM
   * =======================================================
   */

  const modelosFiltrados = useMemo(() => {
    const termo = normalizarTexto(busca);

    return modelos.filter((modelo) => {
      const itensModelo = obterEtapasModalidadesModelo(modelo);

      const nomesEtapas = itensModelo
        .map((item) => obterNomeEtapa(item, etapas))
        .join(" ");

      const correspondeBusca =
        !termo ||
        normalizarTexto(modelo.titulo).includes(termo) ||
        normalizarTexto(modelo.nome_original).includes(termo) ||
        normalizarTexto(modelo.categoria_nome).includes(termo) ||
        normalizarTexto(modelo.assunto_nome).includes(termo) ||
        normalizarTexto(nomesEtapas).includes(termo);

      const correspondeCategoria =
        categoriasFiltro.length === 0 ||
        categoriasFiltro.includes(String(modelo.categoria_id));

      const correspondeAssunto =
        assuntosFiltro.length === 0 ||
        assuntosFiltro.includes(String(modelo.assunto_id ?? ""));

      const idsEtapasModelo = itensModelo.map((item) =>
        String(obterIdEtapa(item)),
      );

      const correspondeEtapa =
        etapasFiltro.length === 0 ||
        etapasFiltro.some((etapaId) =>
          idsEtapasModelo.includes(String(etapaId)),
        );

      return (
        correspondeBusca &&
        correspondeCategoria &&
        correspondeAssunto &&
        correspondeEtapa
      );
    });
  }, [modelos, busca, categoriasFiltro, assuntosFiltro, etapasFiltro, etapas]);

  /*
   * =======================================================
   * ORDENAÇÃO
   * =======================================================
   */

  const modelosOrdenados = useMemo(() => {
    if (!ordem.coluna) {
      return modelosFiltrados;
    }

    const obterValorOrdenacao = (modelo) => {
      if (ordem.coluna === "titulo") {
        return modelo.titulo || "";
      }

      if (ordem.coluna === "categoria") {
        return modelo.categoria_nome || "";
      }

      if (ordem.coluna === "assunto") {
        return modelo.assunto_nome || "";
      }

      return "";
    };

    return [...modelosFiltrados].sort((a, b) => {
      const valorA = obterValorOrdenacao(a);

      const valorB = obterValorOrdenacao(b);

      let resultado = comparar.compare(valorA, valorB);

      if (resultado === 0) {
        resultado = comparar.compare(
          String(a.id ?? ""),

          String(b.id ?? ""),
        );
      }

      return ordem.direcao === "asc" ? resultado : -resultado;
    });
  }, [modelosFiltrados, ordem, comparar]);

  const ordenarPor = (coluna) => {
    setOrdem((atual) => ({
      coluna,

      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPagina(1);
  };

  /*
   * =======================================================
   * CÁLCULO DA PAGINAÇÃO
   * =======================================================
   */

  const totalPaginas = Math.max(
    1,

    Math.ceil(modelosOrdenados.length / itensPorPagina),
  );

  const paginaAtual = Math.min(pagina, totalPaginas);

  const indiceInicial = (paginaAtual - 1) * itensPorPagina;

  const indiceFinal = Math.min(
    indiceInicial + itensPorPagina,

    modelosOrdenados.length,
  );

  const modelosPagina = modelosOrdenados.slice(indiceInicial, indiceFinal);

  /*
   * Volta para a página 1
   * sempre que filtros ou
   * quantidade por página mudarem.
   */
  useEffect(() => {
    setPagina(1);
  }, [busca, categoriasFiltro, assuntosFiltro, etapasFiltro, itensPorPagina]);

  /*
   * Impede uma página inválida
   * após redução da quantidade
   * de resultados.
   */
  useEffect(() => {
    if (pagina > totalPaginas) {
      setPagina(totalPaginas);
    }
  }, [pagina, totalPaginas]);

  /*
   * =======================================================
   * LIMPAR FILTROS
   * =======================================================
   */

  const limparFiltros = () => {
    setBusca("");

    setCategoriasFiltro([]);

    setAssuntosFiltro([]);

    setEtapasFiltro([]);

    setPagina(1);
  };

  const filtrosAtivos =
    Boolean(busca.trim()) ||
    categoriasFiltro.length > 0 ||
    assuntosFiltro.length > 0 ||
    etapasFiltro.length > 0;

  /*
   * =======================================================
   * CABEÇALHO ORDENÁVEL
   * =======================================================
   */

  const renderCabecalhoOrdenavel = (titulo, coluna, Icone) => {
    const ativo = ordem.coluna === coluna;

    return (
      <TableSortLabel
        active={ativo}
        direction={ativo ? ordem.direcao : "asc"}
        onClick={() => ordenarPor(coluna)}
        IconComponent={
          ativo
            ? ordem.direcao === "asc"
              ? ArrowUpward
              : ArrowDownward
            : UnfoldMore
        }
        sx={{
          width: "fit-content",

          color: "#173b72",

          fontSize: ajustarTamanhoFonteSre("0.75rem"),

          fontWeight: 700,

          "&:hover": {
            color: "#0767d8",
          },

          "&.Mui-active": {
            color: "#173b72",
          },

          "& .MuiTableSortLabel-icon": {
            fontSize: 15,

            opacity: 1,

            color: "#0767d8",

            transform: "none !important",

            ml: 0.35,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "flex",

            alignItems: "center",

            gap: 0.65,

            whiteSpace: "nowrap",
          }}
        >
          <Icone
            sx={{
              fontSize: 17,

              color: "#087cf0",

              flexShrink: 0,
            }}
          />

          {titulo}
        </Box>
      </TableSortLabel>
    );
  };

  /*
   * =======================================================
   * CABEÇALHO NÃO ORDENÁVEL
   * =======================================================
   */

  const renderCabecalhoSimples = (titulo, Icone, centralizado = false) => (
    <Box
      sx={{
        display: "flex",

        alignItems: "center",

        justifyContent: centralizado ? "center" : "flex-start",

        gap: 0.65,

        minWidth: 0,

        width: "100%",
      }}
    >
      <Icone
        sx={{
          fontSize: 17,

          color: "#087cf0",

          flexShrink: 0,
        }}
      />

      <Typography
        variant="caption"
        sx={{
          color: "#173b72",

          fontSize: ajustarTamanhoFonteSre("0.75rem"),

          fontWeight: 700,

          whiteSpace: "nowrap",
        }}
      >
        {titulo}
      </Typography>
    </Box>
  );

  /*
   * SRE possui três ações.
   * Analista / Escola possuem
   * apenas download.
   */
  const tituloUltimaColuna = isSuperintendent ? "Ações" : "Baixar";

  const IconeUltimaColuna = isSuperintendent
    ? SettingsRoundedIcon
    : DownloadRoundedIcon;

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      {/* =====================================================
          FILTROS
      ===================================================== */}

      <Box
        sx={{
          border: "1px solid #d7e7f7",

          borderRadius: 2,

          backgroundColor: "#f7fbff",

          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: {
              xs: 2,

              md: 2.25,
            },

            pt: 1.6,

            pb: 1.5,
          }}
        >
          {/* CABEÇALHO DOS FILTROS */}

          <Box
            sx={{
              display: "flex",

              alignItems: "flex-start",

              justifyContent: "space-between",

              gap: 2,

              mb: 1.4,
            }}
          >
            <Box
              sx={{
                display: "flex",

                alignItems: "flex-start",

                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 32,

                  height: 32,

                  borderRadius: 1.25,

                  backgroundColor: "#e8f4ff",

                  color: "#087cf0",

                  display: "grid",

                  placeItems: "center",

                  flexShrink: 0,
                }}
              >
                <FilterAltRoundedIcon fontSize="small" />
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#173b72",

                    fontWeight: 700,

                    lineHeight: 1.15,
                  }}
                >
                  Filtros
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",

                    mt: 0.25,

                    color: "#7890ad",

                    fontSize: ajustarTamanhoFonteSre("0.68rem"),

                    lineHeight: 1.2,
                  }}
                >
                  Refine a busca pelos modelos cadastrados.
                </Typography>
              </Box>
            </Box>

            <Button
              type="button"
              size="small"
              variant="text"
              startIcon={<RestartAltRoundedIcon fontSize="small" />}
              onClick={limparFiltros}
              disabled={!filtrosAtivos}
              sx={{
                mt: -0.25,

                px: 0.5,

                minWidth: 0,

                color: "#0767d8",

                fontSize: ajustarTamanhoFonteSre("0.72rem"),

                fontWeight: 600,

                textTransform: "none",
              }}
            >
              Limpar filtros
            </Button>
          </Box>

          {/* CAMPOS DOS FILTROS */}

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",

                sm: "minmax(0, 1fr) minmax(0, 1fr)",

                lg: "minmax(0, 1.25fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.95fr)",
              },

              gap: 1.3,

              width: "100%",

              minWidth: 0,
            }}
          >
            {/* BUSCAR MODELO */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Buscar modelo</RotuloCampo>

              <TextField
                size="small"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Digite o nome do modelo..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon
                        fontSize="small"
                        sx={{
                          color: "#087cf0",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 0,

                  width: "100%",

                  backgroundColor: "#fff",

                  "& .MuiInputBase-root": {
                    minWidth: 0,
                  },

                  "& .MuiInputBase-input": {
                    minWidth: 0,

                    fontSize: ajustarTamanhoFonteSre("0.82rem"),

                    py: 1,

                    overflow: "hidden",

                    textOverflow: "ellipsis",
                  },
                }}
              />
            </Box>

            {/* CATEGORIA */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Categoria</RotuloCampo>

              <FiltroMultiplo
                placeholder="Todas as categorias"
                itens={categorias}
                selecionados={categoriasFiltro}
                onChange={setCategoriasFiltro}
                icone={
                  <LayersRoundedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                }
              />
            </Box>

            {/* ASSUNTO */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Assunto</RotuloCampo>

              <FiltroMultiplo
                placeholder="Todos os assuntos"
                itens={assuntos}
                selecionados={assuntosFiltro}
                onChange={setAssuntosFiltro}
                icone={
                  <DescriptionOutlinedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                }
              />
            </Box>

            {/* ETAPA / MODALIDADE */}

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <RotuloCampo>Etapa/modalidade</RotuloCampo>

              <FiltroMultiplo
                placeholder="Todas as etapas"
                itens={etapas}
                selecionados={etapasFiltro}
                onChange={setEtapasFiltro}
                icone={
                  <SchoolOutlinedIcon
                    sx={{
                      fontSize: 17,
                    }}
                  />
                }
              />
            </Box>
          </Box>
        </Box>

        {/* =====================================================
            FAIXA INFERIOR DOS FILTROS
        ===================================================== */}

        <Box
          sx={{
            display: "flex",

            alignItems: {
              xs: "flex-start",

              md: "center",
            },

            justifyContent: "space-between",

            flexDirection: {
              xs: "column",

              md: "row",
            },

            gap: 1.25,

            px: {
              xs: 2,

              md: 2.25,
            },

            py: 1.05,

            borderTop: "1px solid #dbe9f6",

            backgroundColor: "#f4f9fe",
          }}
        >
          {/* TOTAL DE REGISTROS */}

          <Typography
            variant="caption"
            sx={{
              color: "#667b98",

              fontSize: ajustarTamanhoFonteSre("0.72rem"),
            }}
          >
            <Box
              component="span"
              sx={{
                color: "#0767d8",

                fontWeight: 700,
              }}
            >
              {modelosFiltrados.length}
            </Box>{" "}
            {modelosFiltrados.length === 1
              ? "registro encontrado"
              : "registros encontrados"}
          </Typography>

          {/* CONTROLES */}

          <Box
            sx={{
              display: "flex",

              alignItems: "center",

              justifyContent: "flex-end",

              flexWrap: "wrap",

              gap: 1.25,

              ml: {
                md: "auto",
              },
            }}
          >
            {/* REGISTROS POR PÁGINA */}

            <Box
              sx={{
                display: "flex",

                alignItems: "center",

                gap: 0.75,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#667b98",

                  fontSize: ajustarTamanhoFonteSre("0.7rem"),

                  whiteSpace: "nowrap",
                }}
              >
                Registros por página:
              </Typography>

              <Select
                size="small"
                value={itensPorPagina}
                onChange={(event) =>
                  setItensPorPagina(Number(event.target.value))
                }
                renderValue={(valor) => `${valor} por página`}
                sx={{
                  minWidth: 118,

                  height: 32,

                  backgroundColor: "#fff",

                  fontSize: ajustarTamanhoFonteSre("0.72rem"),

                  fontWeight: visualPadronizado ? 500 : 600,

                  color: "#3f5576",

                  borderRadius: 1,

                  "& .MuiSelect-select": {
                    py: 0.65,
                  },
                }}
              >
                {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
                  <MenuItem key={quantidade} value={quantidade}>
                    {quantidade} por página
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* PAGINAÇÃO SUPERIOR GLOBAL */}

            <Box sx={separadorPaginacao}>
              <PaginacaoLista
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                ariaLabel="Paginação superior do catálogo de modelos"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* =====================================================
          MODELOS CADASTRADOS
      ===================================================== */}

      <Box
        sx={{
          mt: 1.5,

          border: "1px solid #d7e7f7",

          borderRadius: 2,

          backgroundColor: "#fff",

          overflow: "hidden",
        }}
      >
        {/* CABEÇALHO DO CARD */}

        <Box
          sx={{
            display: "flex",

            alignItems: "center",

            gap: 1,

            px: 2,

            py: 1.15,

            backgroundColor: "#f7fbff",

            borderBottom: "1px solid #d7e7f7",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#173b72",

              fontWeight: 700,
            }}
          >
            Modelos cadastrados
          </Typography>

          <Chip
            size="small"
            label={modelosFiltrados.length}
            sx={{
              height: 21,

              backgroundColor: "#e8f4ff",

              color: "#0767d8",

              fontSize: ajustarTamanhoFonteSre("0.68rem"),

              fontWeight: 700,
            }}
          />
        </Box>

        {/* =====================================================
            TABELA
        ===================================================== */}

        <Box
          sx={{
            width: "100%",

            overflowX: "auto",
          }}
        >
          <Box
            sx={{
              minWidth: 980,
            }}
          >
            {/* CABEÇALHO DA TABELA */}

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: gridColunasModelos,

                width: "100%",

                minHeight: 46,

                alignItems: "center",

                backgroundColor: "#f1f7fd",

                borderBottom: "1px solid #d7e7f7",
              }}
            >
              {/* NOME */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid #dfeaf5",
                }}
              >
                {renderCabecalhoOrdenavel(
                  "Nome do modelo",

                  "titulo",

                  DescriptionOutlinedIcon,
                )}
              </Box>

              {/* CATEGORIA */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid #dfeaf5",
                }}
              >
                {renderCabecalhoOrdenavel(
                  "Categoria",

                  "categoria",

                  LayersRoundedIcon,
                )}
              </Box>

              {/* ASSUNTO */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid #dfeaf5",
                }}
              >
                {renderCabecalhoOrdenavel(
                  "Assunto",

                  "assunto",

                  DescriptionRoundedIcon,
                )}
              </Box>

              {/* ETAPA / MODALIDADE */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 2,

                  borderRight: "1px solid #dfeaf5",
                }}
              >
                {renderCabecalhoSimples(
                  "Etapa/modalidade",

                  SchoolOutlinedIcon,
                )}
              </Box>

              {/* AÇÕES / BAIXAR */}

              <Box
                sx={{
                  minWidth: 0,

                  px: 1,
                }}
              >
                {renderCabecalhoSimples(
                  tituloUltimaColuna,

                  IconeUltimaColuna,

                  true,
                )}
              </Box>
            </Box>

            {/* =================================================
                SEM RESULTADOS
            ================================================= */}

            {modelosFiltrados.length === 0 ? (
              <Box
                sx={{
                  px: 2,

                  py: 4,

                  textAlign: "center",
                }}
              >
                <Alert
                  severity="info"
                  sx={{
                    display: "inline-flex",

                    textAlign: "left",
                  }}
                >
                  Nenhum modelo encontrado com os filtros selecionados.
                </Alert>
              </Box>
            ) : (
              <>
                {/* =============================================
                    LINHAS
                ============================================= */}

                {modelosPagina.map((modelo, index) => (
                  <Box
                    key={modelo.id}
                    sx={{
                      display: "grid",

                      gridTemplateColumns: gridColunasModelos,

                      width: "100%",

                      minHeight: 70,

                      alignItems: "stretch",

                      borderTop: index === 0 ? "none" : "1px solid #e6eef5",

                      backgroundColor: "#fff",

                      "&:hover": {
                        backgroundColor: "#fbfdff",
                      },
                    }}
                  >
                    {/* NOME DO MODELO */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        gap: 1,

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid #e6eef5",
                      }}
                    >
                      <DescriptionRoundedIcon
                        fontSize="small"
                        sx={{
                          color: "#087cf0",

                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        variant="body2"
                        title={modelo.titulo}
                        sx={{
                          ...ESTILO_TEXTO_DUAS_LINHAS,

                          color: "#263238",

                          fontWeight: 500,

                          fontSize: ajustarTamanhoFonteSre("0.82rem"),
                        }}
                      >
                        {modelo.titulo}
                      </Typography>
                    </Box>

                    {/* CATEGORIA */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid #e6eef5",
                      }}
                    >
                      <Chip
                        size="small"
                        label={modelo.categoria_nome || "—"}
                        title={modelo.categoria_nome || ""}
                        sx={{
                          minHeight: 24,

                          height: "auto",

                          maxWidth: "100%",

                          backgroundColor: "#eaf4ff",

                          color: "#0767d8",

                          fontSize: ajustarTamanhoFonteSre("0.72rem"),

                          "& .MuiChip-label": {
                            px: 1.1,

                            py: 0.15,

                            whiteSpace: "normal",

                            lineHeight: 1.25,
                          },
                        }}
                      />
                    </Box>

                    {/* ASSUNTO */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid #e6eef5",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        title={modelo.assunto_nome || "Não informado"}
                        sx={{
                          ...ESTILO_TEXTO_DUAS_LINHAS,

                          fontSize: ajustarTamanhoFonteSre("0.8rem"),
                        }}
                      >
                        {modelo.assunto_nome || "Não informado"}
                      </Typography>
                    </Box>

                    {/* ETAPA / MODALIDADE */}

                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        minWidth: 0,

                        px: 2,

                        py: 1,

                        borderRight: "1px solid #e6eef5",
                      }}
                    >
                      <EtapasCompactas
                        modelo={modelo}
                        etapasCatalogo={etapas}
                      />
                    </Box>

                    {/* AÇÕES / DOWNLOAD */}

                    <Box
                      sx={{
                        display: "flex",

                        justifyContent: "center",

                        alignItems: "center",

                        gap: 0.25,

                        minWidth: 0,

                        px: 0.5,

                        py: 1,
                      }}
                    >
                      {/* DOWNLOAD */}

                      <IconButton
                        size="small"
                        title="Baixar"
                        aria-label="Baixar modelo"
                        onClick={() => onDownload(modelo)}
                        sx={ESTILO_ACAO_DOWNLOAD}
                      >
                        <DownloadRoundedIcon
                          sx={{
                            fontSize: 17,
                          }}
                        />
                      </IconButton>

                      {/* SRE: EDITAR / EXCLUIR */}

                      {isSuperintendent && (
                        <>
                          <IconButton
                            size="small"
                            title="Editar"
                            aria-label="Editar modelo"
                            onClick={() => onEditar?.(modelo)}
                            sx={ESTILO_ACAO_EDITAR}
                          >
                            <EditRoundedIcon
                              sx={{
                                fontSize: 17,
                              }}
                            />
                          </IconButton>

                          <IconButton
                            size="small"
                            title="Excluir"
                            aria-label="Excluir modelo"
                            onClick={() => onExcluir?.(modelo)}
                            sx={ESTILO_ACAO_EXCLUIR}
                          >
                            <DeleteOutlineRoundedIcon
                              sx={{
                                fontSize: 17,
                              }}
                            />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Box>

        {/* =====================================================
            PAGINAÇÃO INFERIOR
        ===================================================== */}

        {modelosFiltrados.length > 0 && (
          <Box
            sx={{
              display: "flex",

              alignItems: {
                xs: "flex-start",

                sm: "center",
              },

              justifyContent: "space-between",

              flexDirection: {
                xs: "column",

                sm: "row",
              },

              gap: 1,

              px: 2,

              py: 1,

              borderTop: "1px solid #dbe9f6",

              backgroundColor: "#f9fbfd",
            }}
          >
            {/* RESUMO */}

            <Typography
              variant="caption"
              sx={{
                color: "#667b98",

                fontSize: ajustarTamanhoFonteSre("0.7rem"),

                whiteSpace: "nowrap",
              }}
            >
              Exibindo{" "}
              <Box
                component="span"
                sx={{
                  color: "#334e6f",

                  fontWeight: 600,
                }}
              >
                {indiceInicial + 1}–{indiceFinal}
              </Box>{" "}
              de{" "}
              <Box
                component="span"
                sx={{
                  color: "#334e6f",

                  fontWeight: 600,
                }}
              >
                {modelosOrdenados.length}
              </Box>{" "}
              modelos
            </Typography>

            {/* PAGINAÇÃO INFERIOR GLOBAL */}

            <Box
              sx={{
                ml: {
                  sm: "auto",
                },
              }}
            >
              <PaginacaoLista
                paginaAtual={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                ariaLabel="Paginação inferior do catálogo de modelos"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CatalogoModelosLista;
